import { Button, Checkbox, FilterGroup, Layout, ResetFilterIcon } from '@utilities';

import {
  type QuotaGroup,
  type SelectedDemographicFilters,
  toFilterGroupValue,
  type UsableDemographicFilter,
} from '../../utilities/filters';

import styles from '../../_index.module.scss';

interface FiltersPanelProps {
  completeRespondentCount: number;
  filteredRespondentCount: number;
  hasActiveFilters: boolean;
  onDemographicFilterChange: (
    filter: UsableDemographicFilter,
    value: Record<string, boolean>
  ) => void;
  onQuotaGroupChange: (group: QuotaGroup) => void;
  onResetFilters: () => void;
  quotaGroups?: QuotaGroup[];
  selectedDemographicFilters: SelectedDemographicFilters;
  selectedQuotaGroups: null | string[];
  usableDemographicFilters: UsableDemographicFilter[];
}

const FiltersPanel = ({
  completeRespondentCount,
  filteredRespondentCount,
  hasActiveFilters,
  onDemographicFilterChange,
  onQuotaGroupChange,
  onResetFilters,
  quotaGroups = [],
  selectedDemographicFilters,
  selectedQuotaGroups,
  usableDemographicFilters,
}: FiltersPanelProps) => (
  <Layout.Flex.Column gap="x-large">
    {quotaGroups.length > 1 && (
      <Layout.Flex.Column gap="small">
        <h4>Quota Group Filters</h4>
        {quotaGroups.map((group) => {
          const value = getQuotaGroupValue(group);

          if (!value) return null;

          return (
            <Checkbox
              data-testid={`turf-simulator-quota-group-filter-${value}`}
              id={`turf-group-filter-${value}`}
              isChecked={selectedQuotaGroups === null || selectedQuotaGroups.includes(value)}
              key={value}
              label={group.name ?? value}
              onChange={() => onQuotaGroupChange(group)}
              title={group.name}
              value={value}
            />
          );
        })}
      </Layout.Flex.Column>
    )}

    {usableDemographicFilters.length > 0 && (
      <Layout.Flex.Column gap="small">
        <h4>Demographic Filters</h4>
        {usableDemographicFilters.map((filter) => (
          <FilterGroup
            filter={{
              ...filter,
              dataTestId: `turf-simulator-demographic-filter-${filter.id}-all`,
              expandButtonDataTestId: `turf-simulator-demographic-filter-${filter.id}-toggle`,
              options: filter.options.map((option) => ({
                dataTestId: `turf-simulator-demographic-filter-${filter.id}-${option.id}`,
                helpText: option.count == null ? undefined : `(${option.count})`,
                id: String(option.id),
                label: option.label,
              })),
            }}
            isDefaultOpen={false}
            key={filter.id}
            onChange={(value) => onDemographicFilterChange(filter, value)}
            value={toFilterGroupValue(selectedDemographicFilters[filter.id])}
          />
        ))}
      </Layout.Flex.Column>
    )}

    <div aria-live="polite" className={styles['turf-simulator-filtered-count']}>
      <strong>n = {filteredRespondentCount}</strong> of {completeRespondentCount} complete
      respondents
    </div>

    <Button
      data-testid="turf-simulator-reset-filters-button"
      icon={<ResetFilterIcon />}
      isDisabled={!hasActiveFilters}
      onClick={onResetFilters}
      text="Reset Filters"
      variant="secondary"
    />
  </Layout.Flex.Column>
);

export const getQuotaGroupValue = (group: QuotaGroup): string =>
  String(group.instantSurveyGroupRef ?? group.id ?? group.name ?? '');

export default FiltersPanel;
